'use server';

import { countFilteredProperties, fetchFilteredProperties } from '@/lib/property-search';
import { SEARCH_KEYS } from '@/lib/search-filters';
import { toCardData, type CardData } from '@/lib/listing-card';
import { MAX_RESULTS_PAGE, RESULTS_PAGE_SIZE } from '@/lib/results-paging';
import { rateLimit } from '@/lib/rate-limit';
import { getClientIp } from '@/lib/request-ip';

// "Show 24 more" under a list of listings: the next page of the same search,
// cut down to what a card shows. The rows are public listings, but the full
// row carries the seller's contacts, which a card never needs.

const ALLOWED = new Set<string>([...SEARCH_KEYS, 'ids', 'sort']);

// A person reading listings presses "Show more" a few times a minute; a script
// walking the whole catalogue does it hundreds of times.
const PER_MINUTE = 60;

export async function loadListingsPage(
  params: Record<string, string>,
  agencyId: string | null,
  page: number
): Promise<{ rows: CardData[]; total: number }> {
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(params || {})) {
    if (ALLOWED.has(key) && typeof value === 'string' && value.length <= 500) clean[key] = value;
  }
  const limited = await rateLimit(`listings-page:${await getClientIp()}`, PER_MINUTE, 60_000);
  if (!limited.ok) throw new Error('rate_limited');

  const n = Math.trunc(Number(page));
  if (!Number.isFinite(n) || n < 1 || n > MAX_RESULTS_PAGE) return { rows: [], total: 0 };

  const result = await fetchFilteredProperties(
    { ...clean, page: String(n), pageSize: String(RESULTS_PAGE_SIZE) },
    typeof agencyId === 'string' && /^[0-9a-f-]{36}$/i.test(agencyId) ? agencyId : undefined
  );
  return { rows: result.rows.map(toCardData), total: result.total };
}

/**
 * How many listings a search would show - the live number on the filter
 * panel's button ("Show 214 listings"), counted by the same rule as the list.
 */
export async function countListings(params: Record<string, string>, agencyId: string | null = null): Promise<number> {
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(params || {})) {
    if (ALLOWED.has(key) && typeof value === 'string' && value.length <= 500) clean[key] = value;
  }
  // Every change in the panel asks, after a short pause.
  const limited = await rateLimit(`listings-count:${await getClientIp()}`, 120, 60_000);
  if (!limited.ok) throw new Error('rate_limited');
  return countFilteredProperties(clean, typeof agencyId === 'string' && /^[0-9a-f-]{36}$/i.test(agencyId) ? agencyId : undefined);
}
